import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { InvoiceClient } from "../../types/invoice";

interface InvoiceClientSelectorProps {
  clients: InvoiceClient[];
  selectedId: string | null;
  onPick: (client: InvoiceClient) => void;
  disabled?: boolean;
}

// Searchable billing-entry dropdown for invoices — adapted from the OCs
// ClientSelector. Options are CompanyBilling entries (grouped by company),
// showing the fiscal name; entries without a Holded contact id are disabled
// with an explanatory caption. The selected `billingId` is what gets sent.
const InvoiceClientSelector: React.FC<InvoiceClientSelectorProps> = ({
  clients,
  selectedId,
  onPick,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");
  const [sortAlpha, setSortAlpha] = useState(true);

  const open = Boolean(anchorEl);

  const selected = useMemo(
    () => clients.find((c) => c.billingId === selectedId) || null,
    [clients, selectedId]
  );

  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.fiscalName.toLowerCase().includes(q) ||
          c.companyLabel.toLowerCase().includes(q) ||
          c.taxId.toLowerCase().includes(q)
      );
    }
    if (sortAlpha) {
      list.sort(
        (a, b) =>
          a.companyLabel.localeCompare(b.companyLabel, "es") ||
          a.fiscalName.localeCompare(b.fiscalName, "es")
      );
    }
    return list;
  }, [clients, search, sortAlpha]);

  // Agrupa por empresa (companyLabel) preservando el orden ya calculado.
  const groupedClients = useMemo(() => {
    const groups: { companyLabel: string; items: InvoiceClient[] }[] = [];
    for (const c of filteredClients) {
      const last = groups[groups.length - 1];
      if (last && last.companyLabel === c.companyLabel) {
        last.items.push(c);
      } else {
        groups.push({ companyLabel: c.companyLabel, items: [c] });
      }
    }
    return groups;
  }, [filteredClients]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch("");
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const displayText = selected
    ? `${selected.fiscalName} · ${selected.companyLabel}`
    : t("invoices.selector.select");

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color: selected ? "inherit" : theme.palette.neutral["600"],
          borderColor: theme.palette.neutral["300"],
        }}
      >
        <Typography
          variant="pSmall"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {displayText}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: anchorEl?.clientWidth, mt: 0.5 } },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t("invoices.selector.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <Typography variant="pSmallest" color={theme.palette.neutral["600"]}>
            {t("invoices.selector.sort")}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortAlpha ? "alpha" : "default"}
            onChange={(_e, v) => {
              if (v !== null) setSortAlpha(v === "alpha");
            }}
          >
            <ToggleButton value="default" sx={{ textTransform: "none" }}>
              {t("invoices.selector.sortDefault")}
            </ToggleButton>
            <ToggleButton value="alpha" sx={{ textTransform: "none" }}>
              {t("invoices.selector.sortAlpha")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <List sx={{ maxHeight: "34rem", overflowY: "auto", py: 0 }}>
          {groupedClients.map((group) => (
            <li key={group.companyLabel}>
              <ul style={{ padding: 0 }}>
                <ListSubheader
                  sx={{
                    bgcolor: theme.palette.neutral["100"],
                    lineHeight: "2rem",
                    fontWeight: 700,
                  }}
                >
                  {group.companyLabel}
                </ListSubheader>
                {group.items.map((c) => (
                  <ListItemButton
                    key={c.billingId}
                    disabled={!c.hasHolded}
                    selected={c.billingId === selectedId}
                    onClick={() => {
                      onPick(c);
                      handleClose();
                    }}
                  >
                    <ListItemText
                      primary={c.fiscalName || c.companyLabel}
                      secondary={
                        !c.hasHolded
                          ? t("invoices.selector.noHolded")
                          : c.taxId || undefined
                      }
                      secondaryTypographyProps={{
                        color: !c.hasHolded
                          ? theme.palette.error.main
                          : theme.palette.neutral["600"],
                        variant: "pSmallest",
                      }}
                    />
                  </ListItemButton>
                ))}
              </ul>
            </li>
          ))}
          {filteredClients.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
                {t("invoices.selector.noResults")}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default InvoiceClientSelector;
